import React from 'react';
import config from 'config';
import formatISO from 'date-fns/formatISO';
import parseISO from 'date-fns/parseISO';
import { has, isEmpty } from 'lodash';
import isEqual from 'lodash/isEqual';

import { BaseEventsUnion } from '@lyearn/academy-app/src/@/hooks/useEventHandler/hooks/useBaseEvents/events';
import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction, Trans } from '@lyearn/i18n';
import {
  Archive,
  ArchiveFill,
  Calendar2,
  Calendar2Outline,
  CheckmarkCircleOutlineLegacy,
  CopyIcon,
  EditOutline,
  EyeOutline,
  PersonEdit,
  PersonOutline,
  PlugOutline,
  Radio,
  RefreshOutline,
  ShareOutline,
  SlashOutline,
  SocialGoogleCalendar,
  SocialOutlook,
  Trash2Fill,
} from '@lyearn/icons';
import { ActionMenuItemType, OnActionMenuItemType } from '@lyearn/molecules';
import { getGoogleCalendarLink } from '@/components/AddToCalendar/utils/googleCalendar';
import { getOutlookCalendarLink } from '@/components/AddToCalendar/utils/outlookCalendarOld';
import { CalendarEventProps } from '@/components/AddToCalendar/utils/types';
import { tagsFilterConfig } from '@/components/FilterManager/configs/tags/tagsFilterConfig';
import { SkillEntityConfig } from '@/components/FilterManager/EntityFilter/entity/skills';
import { UserEntityConfig } from '@/components/FilterManager/EntityFilter/entity/user';
import {
  DEFAULT_SORT_CONFIG,
  getAlphabeticallySortItem,
  getDefaultSortValue,
  getNewestSortItem,
  getOldestSortItem,
} from '@/components/FilterManager/SortMenu/utils';
import {
  FilterManager,
  FilterState,
  SortAndFilterItem,
  SortEnums,
  SortState,
} from '@/components/FilterManager/types';
import { UseWorkspaceAccessControlReturnType } from '@/components/WorkspaceAccessControl/hooks/useWorkspaceAccessControl';
import { AppConfig } from '@/hooks/useAppConfig';
import { WorkspaceAccessibleValidationType } from '@/hooks/useEventHandler/validations/WorkspaceAccessible';
import { UseUserContextType } from '@/modules/auth/hooks/useUser';
import { ShareMenuActions } from '@/modules/lyearn-connect/types';
import routes from '@/pages/corporate/routes';
import {
  AssignmentTypeEnum,
  CronFrequencyEnum,
  DateFilter,
  EventStatusEnum,
  EventTypeEnum,
  EventUnion,
  OrderEnum,
  RecurringTypeEnum,
  TagAvailableOnEnum,
  WorkspaceActions,
  WorkspaceSubjects,
} from '@/types/schema';
import { getCurrentTimeZoneValue } from '@/utils/timezone';

import { EventInstructorFragment } from '../../../graphql/fragments/generated/EventInstructor';
import { EventsManagerComponentType, EventsManagerStateType } from '../hooks/useEventAction';
import { ActionDialog, EventAction, SessionTabs } from '../types';
import getEventFilterTypes from './getEventFilterTypes';

const EVENT_SORT = {
  ...DEFAULT_SORT_CONFIG,
  [SortEnums.Upcoming]: [{ field: 'startTime', order: OrderEnum.Asc }],
};

const getShareMenuItemsNew = ({
  t,
  showLyearnConnect,
  event,
}: {
  t: TFunction;
  showLyearnConnect: boolean;
  event: EventUnion;
}): (OnActionMenuItemType<BaseEventsUnion<EventsManagerStateType, EventsManagerComponentType>> & {
  enabled: boolean;
})[] => {
  const readValidaiton: WorkspaceAccessibleValidationType = {
    type: 'workspace-accessible',
    params: {
      snackbarProps: ['copy', 'content'],
      workspaceAction: WorkspaceActions.Read,
      workspaceSubject: WorkspaceSubjects.Event,
    },
  };

  return [
    {
      id: ShareMenuActions.ShareLearnerLink,
      label: t('Common:CopyLearnersLink'),
      EndIcon: PersonOutline,
      enabled: true,
      eventHandler: {
        type: 'copy-route-to-clipboard',
        params: {
          url: routes.EventDetails(event.id),
        },
        validations: [readValidaiton],
      },
    },
    {
      id: ShareMenuActions.ShareStaffLink,
      label: t('Common:CopyStaffLink'),
      EndIcon: PersonEdit,
      enabled: true,
      eventHandler: {
        type: 'copy-route-to-clipboard',
        params: {
          url: routes.AdminEventDetails(event.id),
          query: {
            staff: 'true',
          },
        },
        validations: [readValidaiton],
      },
    },
    {
      id: ShareMenuActions.ShareContent,
      label: t('Admin:ShareActions.ShareContent'),
      EndIcon: PlugOutline,
      enabled: showLyearnConnect,
      eventHandler: {
        type: 'show-component',
        params: {
          Component: ActionDialog.Share,
          props() {
            return {
              event,
            };
          },
        },
        validations: [
          {
            type: 'workspace-accessible',
            params: {
              workspaceAction: WorkspaceActions.Read,
              workspaceSubject: WorkspaceSubjects.Event,
              snackbarProps: ['share', 'session'],
            },
          },
        ],
      },
    },
  ];
};

export const getEventSortItems = (t: TFunction, activeTab: SessionTabs) => [
  getNewestSortItem(),
  getOldestSortItem(),
  {
    id: SortEnums.Upcoming,
    Icon: Calendar2,
    label: t('Common:Sort.Upcoming'),
    sort: EVENT_SORT[SortEnums.Upcoming],
    enabled: activeTab === SessionTabs.Upcoming,
  },
  getAlphabeticallySortItem(),
];

export const getEventSortDefaultValue = (t: TFunction, activeTab: SessionTabs) =>
  getDefaultSortValue(getEventSortItems(t, activeTab));

interface GetEventTypeFilterItemParams {
  id?: string;
  t: TFunction;
}

export const getEventTypeFilterItem = (
  { id = 'types', t }: GetEventTypeFilterItemParams,
  appConfig: AppConfig,
  activeTab?: SessionTabs,
): SortAndFilterItem => ({
  id,
  type: FilterManager.Filter,
  defaultPlaceholder: t('Common:Filter.Type'),
  isEnabled: true,
  groups: [
    {
      id: 'types',
      options: getEventFilterTypes(
        t,
        appConfig,
        activeTab === SessionTabs.Recurring ? 'recurring' : 'non-recurring',
      ),
    },
  ],
});

export const getSortAndFilterItems = (
  t: TFunction,
  activeTab: SessionTabs,
  appConfig: AppConfig,
): SortAndFilterItem[] => {
  return [
    {
      type: FilterManager.Sort,
      isEnabled: true,
      items: getEventSortItems(t, activeTab),
    },
    {
      type: FilterManager.Entity,
      isEnabled: activeTab !== SessionTabs.Recurring,
      listConfig: UserEntityConfig,
      defaultPlaceholder: t('Common:Filter.Instructor'),
      id: 'instructorIds',
    },
    {
      type: FilterManager.Filter,
      id: 'recurringFrequencies',
      defaultPlaceholder: t('Common:Frequency.Frequency'),
      isEnabled: activeTab === SessionTabs.Recurring,
      groups: [
        {
          id: 'recurringFrequencies',
          options: [
            {
              value: CronFrequencyEnum.Daily,
              label: t('Common:Frequency.Daily'),
            },
            {
              value: CronFrequencyEnum.Weekly,
              label: t('Common:Frequency.Weekly'),
            },
            {
              value: CronFrequencyEnum.Monthly,
              label: t('Common:Frequency.Monthly'),
            },
            {
              value: CronFrequencyEnum.Custom,
              label: t('Common:Frequency.Custom'),
            },
          ],
        },
      ],
    },
    getEventTypeFilterItem({ t }, appConfig, activeTab),
    {
      type: FilterManager.Filter,
      id: 'status',
      defaultPlaceholder: t('Common:Sort.Status'),
      isEnabled: true,
      groups: [
        {
          id: 'eventStatus',
          options: getEnabledItems([
            {
              Icon: Calendar2,
              value: EventStatusEnum.Scheduled,
              label: t('Common:Sort.Upcoming'),
              enabled: [SessionTabs.Upcoming, SessionTabs.Recurring].includes(activeTab),
            },
            {
              Icon: Radio,
              value: EventStatusEnum.Started,
              label: t('Common:Filter.Live'),
              enabled: activeTab === SessionTabs.Upcoming,
            },
            {
              Icon: CheckmarkCircleOutlineLegacy,
              value: EventStatusEnum.Completed,
              label: t('Common:Filter.Finished'),
              enabled: [SessionTabs.Finished, SessionTabs.Recurring].includes(activeTab),
            },
            {
              Icon: Archive,
              value: EventStatusEnum.Archived,
              label: t('Common:Filter.Canceled'),
              enabled: [SessionTabs.Finished, SessionTabs.Recurring].includes(activeTab),
            },
            {
              Icon: SlashOutline,
              value: EventStatusEnum.Revoked,
              label: t('Common:Filter.Revoked'),
              enabled: activeTab === SessionTabs.Finished,
            },
          ]),
        },
      ],
    },
    {
      type: FilterManager.Entity,
      isEnabled: activeTab !== SessionTabs.Recurring,
      id: 'skillIds',
      defaultPlaceholder: t('Common:Filter.Skills'),
      entity: 'skill',
      listConfig: SkillEntityConfig,
    },
    {
      type: FilterManager.DateRange,
      isEnabled: activeTab !== SessionTabs.Recurring,
      id: 'startTime',
      defaultPlaceholder: t('Common:Filter.SessionStartDate'),
    },
    {
      ...tagsFilterConfig(t),
      queryParams: {
        availableOn: [TagAvailableOnEnum.Event],
      },
    },
  ];
};

const getUpcomingEventsFilter = () => {
  return {
    absolute: {
      startTime: formatISO(new Date()),
    },
    timezone: getCurrentTimeZoneValue(),
  } as DateFilter;
};

const UPCOMING_STATUSES = [
  EventStatusEnum.Scheduled,
  EventStatusEnum.Started,
  EventStatusEnum.Waiting,
];

export const getFilters = (filter: FilterState, sort: SortState, activeTab: SessionTabs) => {
  const filterCopy = { ...filter };

  if (isEqual(sort, EVENT_SORT[SortEnums.Upcoming]) && !has(filterCopy, 'startTime')) {
    filterCopy.startTime = getUpcomingEventsFilter();
  }

  if (!has(filterCopy, 'status') || isEmpty(filterCopy.status)) {
    if (activeTab === SessionTabs.Upcoming) {
      filterCopy.status = UPCOMING_STATUSES;
    } else if (activeTab === SessionTabs.Finished) {
      filterCopy.status = [
        EventStatusEnum.Archived,
        EventStatusEnum.Completed,
        EventStatusEnum.Revoked,
      ];
    } else {
      filterCopy.status = [
        EventStatusEnum.Archived,
        EventStatusEnum.Completed,
        EventStatusEnum.Scheduled,
        EventStatusEnum.Started,
        EventStatusEnum.Waiting,
      ];
    }
  }

  if (!has(filterCopy, 'types') || isEmpty(filterCopy.types)) {
    if (activeTab === SessionTabs.Recurring) {
      filterCopy.types = [
        EventTypeEnum.RecurringLive,
        EventTypeEnum.RecurringExternal,
        EventTypeEnum.RecurringOffline,
      ];
    } else {
      filterCopy.types = [EventTypeEnum.Live, EventTypeEnum.External, EventTypeEnum.Offline];
    }
  }

  return filterCopy;
};

export const getEventActionItems = ({
  t,
  isWorkspaceAccessible,
  showLyearnConnect,
  allowedActions,
  isDetailPage,
  event,
  currentUser,
}: {
  t: TFunction;
  allowedActions?: string[];
  showLyearnConnect: boolean;
  isWorkspaceAccessible: UseWorkspaceAccessControlReturnType['isAccessible'];
  isDetailPage: boolean;
  event: EventUnion;
  currentUser: UseUserContextType;
}) => {
  const { status, sharedByWorkspace, recurringCategory } = event;
  const canReadEvent = isWorkspaceAccessible(WorkspaceActions.Read, WorkspaceSubjects.Event);
  const isNotArchived = status !== EventStatusEnum.Archived;

  if (!status) {
    return [];
  }

  const editValidation: WorkspaceAccessibleValidationType = {
    type: 'workspace-accessible',
    params: {
      workspaceAction: WorkspaceActions.Edit,
      workspaceSubject: WorkspaceSubjects.Event,
      snackbarProps: ['edit', 'session'],
    },
  };

  const viewValidation: WorkspaceAccessibleValidationType = {
    type: 'workspace-accessible',
    params: {
      workspaceAction: WorkspaceActions.Read,
      workspaceSubject: WorkspaceSubjects.Event,
      snackbarProps: ['view', 'session'],
    },
  };

  function getCalendarProps(): CalendarEventProps {
    const html_link = `${config.origin}${routes.EventDetails(event.id)}`;
    return {
      title: event.name,
      organiser: currentUser,
      start: parseISO(event.startTime).toUTCString(),
      end: parseISO(event.endTime).toUTCString(),
      guests: [],
      description: `<a href="${html_link}">${html_link}</a><br /><br />${event.description ?? ''}`,
    };
  }

  const enabledItems = getEnabledItems<
    OnActionMenuItemType<BaseEventsUnion<EventsManagerStateType, EventsManagerComponentType>>
  >([
    {
      id: EventAction.View,
      label: t('Common:Actions.View'),
      EndIcon: EyeOutline,
      enabled: true,
      eventHandler: {
        type: 'push-navigation',
        validations: [viewValidation],
        params: {
          url: routes.AdminEventAttendees(event.latestUpcomingEventId ?? event.id),
        },
      },
    },
    {
      id: EventAction.Edit,
      label: t('Common:Actions.Edit'),
      EndIcon: EditOutline,
      enabled:
        !sharedByWorkspace &&
        recurringCategory !== RecurringTypeEnum.RecurringInstance &&
        (status === EventStatusEnum.Scheduled || status === EventStatusEnum.Completed),
      eventHandler: {
        type: 'push-navigation',
        validations: [editValidation],
        params: {
          url: routes.AdminEventEditor(event.id),
        },
      },
    },
    {
      id: EventAction.EditRecurringEvent,
      label: t('Events:EditRecurringSession'),
      EndIcon: EditOutline,
      enabled:
        isDetailPage &&
        !sharedByWorkspace &&
        recurringCategory === RecurringTypeEnum.RecurringInstance &&
        !!event.parentEventId,
      eventHandler: {
        type: 'push-navigation',
        validations: [editValidation],
        params: {
          url: routes.AdminEventEditor(event.parentEventId ?? ''),
        },
      },
    },
    {
      id: EventAction.Reschedule,
      label: t('Common:Actions.Reschedule'),
      EndIcon: RefreshOutline,
      enabled:
        !sharedByWorkspace &&
        recurringCategory === RecurringTypeEnum.NonRecurring &&
        status === EventStatusEnum.Scheduled,
      eventHandler: {
        type: 'show-component',
        validations: [editValidation],
        params: {
          Component: ActionDialog.Reschedule,
          props: () => ({ event }),
        },
      },
    },
    {
      id: EventAction.RescheduleSlot,
      label: isDetailPage ? t('Events:RescheduleSlot') : t('Common:Actions.Reschedule'),
      EndIcon: RefreshOutline,
      enabled:
        !sharedByWorkspace &&
        recurringCategory === RecurringTypeEnum.RecurringInstance &&
        status === EventStatusEnum.Scheduled,
      eventHandler: {
        type: 'show-component',
        validations: [editValidation],
        params: {
          Component: ActionDialog.Reschedule,
          props: () => ({
            event,
          }),
        },
      },
    },
    {
      id: ShareMenuActions.OpenShareMenu,
      type: 'item',
      label: t('Common:Share'),
      EndIcon: ShareOutline,
      enabled: isNotArchived && canReadEvent && !isDetailPage,
      items: getEnabledItems(
        getShareMenuItemsNew({
          t,
          showLyearnConnect,
          event,
        }),
      ),
    },
    ...(isDetailPage
      ? getShareMenuItemsNew({
          t,
          showLyearnConnect,
          event,
        })
      : []),
    {
      id: EventAction.Duplicate,
      label: t('Common:Actions.Duplicate'),
      EndIcon: CopyIcon,
      enabled: !sharedByWorkspace,
      eventHandler: {
        validations: [
          {
            type: 'workspace-accessible',
            params: {
              workspaceAction: WorkspaceActions.Create,
              workspaceSubject: WorkspaceSubjects.Event,
              snackbarProps: ['duplicate', 'session'],
            },
          },
        ],
        type: 'push-navigation',
        params: {
          url: routes.AdminEventDuplicator(event.id),
        },
      },
    },
    {
      id: EventAction.ADD_TO_CALENDAR,
      label: t('Common:Actions.AddToCalendar'),
      EndIcon: Calendar2Outline,
      enabled: UPCOMING_STATUSES.includes(status),
      items: [
        {
          id: EventAction.ADD_TO_CALENDAR_GOOGLE,
          label: t('Common:Actions.GoogleCalendar'),
          EndIcon: SocialGoogleCalendar,
          eventHandler: {
            type: 'open-link',
            params: {
              getUrl: () => getGoogleCalendarLink(getCalendarProps()),
            },
          },
        },
        {
          id: EventAction.ADD_TO_CALENDAR_OUTLOOK,
          label: t('Common:Actions.MicrosoftOutlook'),
          EndIcon: SocialOutlook,
          eventHandler: {
            type: 'open-link',
            params: {
              getUrl: () => getOutlookCalendarLink(getCalendarProps()),
            },
          },
        },
      ],
    },
    {
      id: EventAction.Delete,
      appearance: 'danger',
      label: t('Common:Delete'),
      EndIcon: Archive,
      enabled:
        (status === EventStatusEnum.Completed || status === EventStatusEnum.Archived) &&
        !sharedByWorkspace,
    },
    {
      id: EventAction.Cancel,
      appearance: 'danger',
      label: t('Common:Cancel'),
      EndIcon: Archive,
      enabled: status === EventStatusEnum.Scheduled && !sharedByWorkspace,
    },
  ]);

  if (allowedActions?.length) {
    return enabledItems.filter((item) => allowedActions.includes(item.id));
  }

  return enabledItems;
};

export const getEventLearnerActionItems = (t: TFunction, status: EventStatusEnum | null) => {
  if (!status) {
    return [];
  }

  return getEnabledItems([
    {
      id: EventAction.LearnerDuplicate,
      label: t('Common:Actions.Duplicate'),
      EndIcon: CopyIcon,
      enabled: true,
    },
    {
      id: ShareMenuActions.LearnerCopyLink,
      label: t('Common:CopyLink'),
      EndIcon: ShareOutline,
      enabled: status !== EventStatusEnum.Archived,
    },
    {
      id: EventAction.LearnerCancel,
      appearance: 'danger',
      label: t('Common:Cancel'),
      EndIcon: Archive,
      enabled: status === EventStatusEnum.Scheduled,
    },
  ]) as ActionMenuItemType[];
};

export const getUsersName = (t: TFunction, users?: EventInstructorFragment[]) => {
  if (!users || !users.length) {
    return null;
  }

  if (users.length === 1) {
    return users[0].name;
  }

  if (users.length === 2) {
    return t('Events:TwoUsers', { user_1: users[0].name, user_2: users[1].name });
  }

  if (users.length > 2) {
    return t('Events:MultipleUsers', { name: users[0].name, count: users.length - 1 });
  }
};

export const getSnackbarArchiveIcon = () => (
  <ArchiveFill className="h-20 w-20" fillColorToken="icon-placeholder" />
);

export const getSnackbarDeleteIcon = () => (
  <Trash2Fill className="h-20 w-20" fillColorToken="icon-placeholder" />
);

export const getSnackbarDeleteText = (eventName: string) => (
  <Trans i18nKey="Events:SessionDeleted" values={{ eventName }} />
);

export const ASSIGNMENT_TYPE_LABEL_MAP: Record<AssignmentTypeEnum, string> = {
  [AssignmentTypeEnum.All]: 'Events:RecurringSession.AllSlots',
  [AssignmentTypeEnum.Flexible]: 'Events:RecurringSession.FlexibleSlots',
  [AssignmentTypeEnum.Specific]: 'Events:RecurringSession.SpecificSlots',
};
